
import AppHeader from "@/components/AppHeader.vue";
import Error from "@/components/Error.vue";
import VDModal from "@/components/modals/VDModal";
import Page from "@/components/Page.vue";
import User from "@/views/User/User.vue";
import ResetPasswordModal from "@/components/modals/ResetPasswordModal.vue";
import OrganizationModal from "@/components/modals/OrganizationModal.vue";
import useUserSidebar from "@/views/User/useUserSidebar";
import useError from "@/composables/useError";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Pagination from "@/components/pagination/Pagination.vue";
import isEqual from "lodash/isEqual";
import AssignUserRoleOnBehalfOfModal from "@/components/modals/AssignUserRoleOnBehalfOfModal/AssignUserRoleOnBehalfOfModal.vue";
import {
  AssignUserRoleOnBehalfOfInputfType,
  OrganizationType,
  PageParamsInputType,
  useGetOrganizationQuery,
  UserType,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { FilterMatchMode } from "primevue/api";
import { defineComponent, ref, watch, computed, onUnmounted } from "vue";
import useUser from "@/composables/useUser";
import { useRoute, useRouter } from "vue-router";
import { PageState } from "primevue/paginator";
import {
  displayUserCreationDate,
  totalDaysSinceLastLogin,
} from "@/composables/utils";
import useExportAllProfilesToExcel from "@/composables/useExportAllProfilesToExcel";
import {
  UKENDT_ORGANIZATION,
  UKENDT_ORGANIZATION_EU,
  UKENDT_ORGANIZATION_NON_EU,
} from "@/constants";

// Query
gql`
  query GetOrganization(
    $organizationId: ID
    $pageParams: PageParamsInputType!
  ) {
    organization(organizationId: $organizationId) {
      name
      organizationId
      vat
      notifiedOnChange
      notifiedOnUserChange
      notifiedOnRoleAssignmentChange
      users(pageParams: $pageParams) {
        total
        items {
          ...OrganizationUserParts
        }
      }
      oboUsers(pageParams: $pageParams) {
        total
        items {
          ...OrganizationUserParts
        }
      }
      assigneeRoles {
        roleId
        roleName
        isOnBehalf
        applicationId
        applicationName
        organizationId
        organizationName
      }
    }
  }
`;

export default defineComponent({
  inheritAttrs: false,
  components: {
    AppHeader,
    Page,
    Error,
    OrganizationModal,
    ResetPasswordModal,
    Pagination,
    AssignUserRoleOnBehalfOfModal,
    User,
    TabView,
    TabPanel,
  },
  name: "organization",
  props: {
    organizationId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { user } = useUser();

    // Role-based computed properties
    const canManageUsers = computed(
      () => user.value?.isSuperAdmin || user.value?.isAdmin || false
    );

    const canManageUsersRole = computed(
      () =>
        user.value?.isSuperAdmin ||
        user.value?.isUserAdmin ||
        user.value?.isAdmin ||
        user.value?.isApplicationAdmin ||
        false
    );

    const canResetPassword = (orgVat: any): boolean => {
      // When organization is 99999997, only super admin and application admin can reset password
      if (orgVat === UKENDT_ORGANIZATION_NON_EU) {
        return (
          user.value?.isSuperAdmin || user.value?.isApplicationAdmin || false
        );
      }
      // For other organizations, super admin and admin can reset password
      return isNotUkendtOrganisation(orgVat) && canManageUsers.value;
    };

    const activeTab = ref(0);
    const organizationModal = ref<VDModal<OrganizationType> | null>(null);
    const resetPasswordModal = ref<VDModal<UserType> | null>(null);
    const userSidebar = useUserSidebar();
    const route = useRoute();
    const router = useRouter();
    const pageParams = ref<PageParamsInputType>({
      pageSize: 10,
      currentPage: 1,
      filter: "",
    });

    const searchText = ref("");
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    watch(searchText, (val) => {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        pageParams.value.filter = val;
        pageParams.value.currentPage = 1;
        refetch({ pageParams: pageParams.value });
      }, 500);
    });

    const { result, loading, error, refetch } = useGetOrganizationQuery(
      { organizationId: props.organizationId, pageParams: pageParams.value },
      {
        fetchPolicy: "no-cache",
      }
    );

    const firstLoad = ref(false);
    watch(result, (v) => {
      if (v && !firstLoad.value) firstLoad.value = true;
    });

    const assignUserRoleOnBehalfOfModal = ref<VDModal<OrganizationType> | null>(
      null
    );
    function onPageChange(event: PageState) {
      pageParams.value.pageSize = event.rows;
      pageParams.value.currentPage = event.page + 1;
      refetch({ pageParams: pageParams.value });
    }

    watch(
      () => route.params,
      async (newValue) => {
        const hasOnlyOrganizationId =
          Object.keys(newValue).length === 1 &&
          Object.prototype.hasOwnProperty.call(newValue, "organizationId");

        if (!hasOnlyOrganizationId) {
          return;
        }

        try {
          await refetch({
            organizationId: newValue.organizationId.toString(),
            pageParams: pageParams.value,
          });
        } catch (error) {
          console.error(error);
        }
      }
    );

    // Add reloadTables handler
    function reloadTables() {
      refetch({
        organizationId: props.organizationId,
        pageParams: pageParams.value,
      });
    }

    const { exportSingleOrg, isExporting: exportUserProfilesToExcelLoading } =
      useExportAllProfilesToExcel();

    function exportUserProfilesToExcel() {
      exportSingleOrg(props.organizationId);
    }

    onUnmounted(() => {
      if (searchTimeout) clearTimeout(searchTimeout);
    });

    function canCreateProfile(vat: any): boolean {
      return vat !== UKENDT_ORGANIZATION && vat !== UKENDT_ORGANIZATION_EU;
    }

    function isNotUkendtOrganisation(vat: any): boolean {
      return (
        vat !== UKENDT_ORGANIZATION &&
        vat !== UKENDT_ORGANIZATION_EU &&
        vat !== UKENDT_ORGANIZATION_NON_EU
      );
    }

    return {
      user,
      canManageUsers,
      canManageUsersRole,
      canResetPassword,
      resetPasswordModal,
      organizationModal,
      organization: useResult(result),
      error: useError(error).value || undefined,
      loading,
      pageParams,
      searchText,
      firstLoad,
      filters: useFilters(),
      userSidebar,
      activeTab,
      assignUserRoleOnBehalfOfModal,
      onPageChange,
      reloadTables,
      isEqual: isEqual,
      exportUserProfilesToExcel,
      totalDaysSinceLastLogin,
      displayUserCreationDate,
      exportUserProfilesToExcelLoading,
      isNotUkendtOrganisation,
      canCreateProfile: canCreateProfile,
    };
  },
});

function useFilters() {
  return ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
}
